/* eslint-disable no-undef */
import React, { ReactNode } from 'react';
import { Link as MuiLink, LinkProps as MuiLinkProps } from '@mui/material';
import { useAppNavigation } from '../../hooks/useAppNavigation';

export interface AppLinkProps extends Omit<MuiLinkProps, 'href' | 'onClick'> {
  to: string;
  children: ReactNode;
  replace?: boolean;
  state?: any;
  resetScroll?: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * AppLink is a standardized link component that replaces RouterLink
 * throughout the application. It uses the custom useAppNavigation hook
 * to ensure consistent navigation behavior and proper route cleanup.
 */
export const AppLink = React.forwardRef<HTMLAnchorElement, AppLinkProps>(({
  to,
  children,
  replace = false,
  state,
  resetScroll = true,
  onClick,
  ...muiLinkProps
}, ref) => {
  const { navigateTo } = useAppNavigation();

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    // Call custom onClick if provided
    if (onClick) {
      onClick(event);
    }

    // Navigate using our custom hook
    navigateTo(to, {
      replace,
      state,
      resetScroll,
    });
  };

  return (
    <MuiLink
      ref={ref}
      href={to}
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        textDecoration: 'none',
        ...muiLinkProps.sx,
      }}
      {...muiLinkProps}
    >
      {children}
    </MuiLink>
  );
});

AppLink.displayName = 'AppLink';